import { isInIframe } from "@/utils/helper";
import { STORED_APP_VERSION_KEY } from "@/utils/versionJsonLog";
import { logEvent } from "@/utils/activityLog";

/** In-memory dedup for the current page session (sessionStorage is cleared on invoice URLs). */
let loggedIntegrationSessionKey = null;

let parentIntegration = null;

export const INTEGRATION_TYPE = {
  WOOCOMMERCE: "woocommerce",
  INVOICE: "invoice",
  INVOICE_IFRAME: "invoice-iframe",
  JS_SDK: "js-sdk",
};

/**
 * @param {{ integrationType?: string, integrationVersion?: string, parentDomain?: string }} info
 */
export function setParentIntegration(info = {}) {
  if (!info || typeof info !== "object") {
    return;
  }
  parentIntegration = {
    integrationType:
      info.integrationType || parentIntegration?.integrationType || undefined,
    integrationVersion:
      info.integrationVersion ||
      parentIntegration?.integrationVersion ||
      undefined,
    parentDomain:
      info.parentDomain || parentIntegration?.parentDomain || undefined,
  };
}

export function resolveCartMerchantId(cart = {}) {
  return cart.merchant_id || cart.merchantId || null;
}

export function applyIntegrationFromCart(cart = {}) {
  const integration = cart.integration || {};
  const integrationType =
    integration.integrationType || cart.integrationType || undefined;
  const integrationVersion =
    integration.integrationVersion || cart.integrationVersion || undefined;
  const parentDomain =
    integration.parentDomain || cart.parentDomain || undefined;

  if (integrationType || integrationVersion || parentDomain) {
    setParentIntegration({
      integrationType,
      integrationVersion,
      parentDomain,
    });
  }
}

/**
 * Parent embeds (WooCommerce, JS SDK) can be any merchant origin, so there is
 * no host allowlist. Policy: require a parseable http(s) origin attested by
 * the browser via event.origin (never trust payload-supplied origins).
 */
function isAllowedParentOrigin(origin) {
  if (!origin || origin === "null") {
    return false;
  }
  try {
    const url = new URL(origin);
    return url.protocol === "https:" || url.protocol === "http:";
  } catch {
    return false;
  }
}

function parentDomainFromEventOrigin(origin) {
  try {
    return new URL(origin).host;
  } catch {
    return undefined;
  }
}

export function applyIntegrationFromParentMessage(event) {
  if (!event || typeof window === "undefined") {
    return;
  }

  // Only accept messages from the immediate embedding parent
  if (!isInIframe() || event.source !== window.parent) {
    return;
  }

  if (!isAllowedParentOrigin(event.origin)) {
    return;
  }

  const data = event.data;
  if (!data || typeof data !== "object") {
    return;
  }

  const parentDomain = parentDomainFromEventOrigin(event.origin);

  if (data.type === "rocketfuel_integration_context") {
    setParentIntegration({
      integrationType: data.integrationType,
      integrationVersion: data.integrationVersion,
      parentDomain,
    });
    return;
  }

  if (data.type === "parent_origin") {
    setParentIntegration({
      integrationType:
        parentIntegration?.integrationType || INTEGRATION_TYPE.JS_SDK,
      parentDomain,
    });
  }
}

function extractHostedPageId(pathname) {
  const match = pathname.match(
    /^\/(?:select-currency|hostedPage)\/([0-9a-fA-F-]+)/
  );
  return match ? match[1] : null;
}

export function getEntryFlow(pathname = window.location.pathname) {
  if (/^\/select-currency\//.test(pathname)) {
    return "select-currency-url";
  }
  if (/^\/hostedPage\//.test(pathname)) {
    return "hosted-page-url";
  }
  if (isInIframe()) {
    return "iframe-embed";
  }
  return "direct";
}

function isInvoiceUrlFlow(entryFlow) {
  return entryFlow === "select-currency-url" || entryFlow === "hosted-page-url";
}

/**
 * Merchant integration channel for analytics.
 * - woocommerce: WooCommerce plugin iframe (cart/postMessage context)
 * - invoice: /select-currency or /hostedPage opened in a top-level browser window
 * - invoice-iframe: same invoice URLs embedded in a merchant iframe
 * - js-sdk: RocketFuel JS SDK embed
 */
export function resolveIntegrationType(extra = {}) {
  const pathname =
    extra.pathname ||
    (typeof window !== "undefined" ? window.location.pathname : "");
  const entryFlow = extra.entryFlow || getEntryFlow(pathname);
  const inIframe =
    extra.inIframe ??
    (typeof window !== "undefined" ? isInIframe() : false);

  const platformType =
    parentIntegration?.integrationType || extra.integrationType;

  if (platformType === INTEGRATION_TYPE.WOOCOMMERCE) {
    return INTEGRATION_TYPE.WOOCOMMERCE;
  }
  if (platformType === INTEGRATION_TYPE.JS_SDK) {
    return INTEGRATION_TYPE.JS_SDK;
  }

  if (isInvoiceUrlFlow(entryFlow)) {
    return inIframe ? INTEGRATION_TYPE.INVOICE_IFRAME : INTEGRATION_TYPE.INVOICE;
  }

  if (inIframe) {
    return INTEGRATION_TYPE.INVOICE_IFRAME;
  }

  return INTEGRATION_TYPE.INVOICE;
}

function getPaymentPageVersion() {
  try {
    return localStorage.getItem(STORED_APP_VERSION_KEY) || undefined;
  } catch {
    return undefined;
  }
}

export function getIntegrationLogQueryParams(extra = {}) {
  const pathname =
    extra.pathname ||
    (typeof window !== "undefined" ? window.location.pathname : "");
  const entryFlow = extra.entryFlow || getEntryFlow(pathname);
  const integrationType =
    extra.integrationType ||
    resolveIntegrationType({ ...extra, pathname, entryFlow });
  const paymentPageVersion = getPaymentPageVersion();

  return {
    integrationType,
    entryFlow,
    integrationVersion:
      extra.integrationVersion ||
      parentIntegration?.integrationVersion ||
      paymentPageVersion ||
      null,
    parentDomain: extra.parentDomain || parentIntegration?.parentDomain || null,
    inIframe:
      extra.inIframe ??
      (typeof window !== "undefined" ? isInIframe() : false),
  };
}

export function buildActivityIntegrationMeta(extra = {}) {
  const pathname =
    extra.pathname ||
    (typeof window !== "undefined" ? window.location.pathname : "");
  const paymentPageVersion = getPaymentPageVersion();
  const entryFlow = extra.entryFlow || getEntryFlow(pathname);
  const hostedPageId =
    extra.hostedPageId || extractHostedPageId(pathname) || undefined;
  const merchantId = extra.merchantId || null;
  const integrationType =
    extra.integrationType ||
    resolveIntegrationType({ ...extra, pathname, entryFlow });
  let integrationVersion =
    extra.integrationVersion || parentIntegration?.integrationVersion;

  if (!integrationVersion && integrationType === INTEGRATION_TYPE.INVOICE) {
    integrationVersion = paymentPageVersion;
  }

  return {
    pathname,
    entryFlow,
    integrationType,
    integrationVersion: integrationVersion || paymentPageVersion || null,
    paymentPageVersion: paymentPageVersion || null,
    inIframe:
      extra.inIframe ??
      (typeof window !== "undefined" ? isInIframe() : false),
    hostDomain: typeof window !== "undefined" ? window.location.host : null,
    parentDomain:
      extra.parentDomain || parentIntegration?.parentDomain || null,
    hostedPageId: hostedPageId || null,
    merchantId,
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
  };
}

/**
 * Explicit activity log for integration/session context via POST /addActivity.
 */
async function logActivityIntegrationStartInner(extra = {}) {
  const merchantId = extra.merchantId || resolveCartMerchantId(extra);
  if (!merchantId) {
    return;
  }
  const meta = buildActivityIntegrationMeta({ ...extra, merchantId });
  const sessionKey = [
    meta.entryFlow,
    meta.hostedPageId || meta.pathname,
    meta.integrationType,
    meta.integrationVersion || "",
    meta.parentDomain || "",
    merchantId,
  ].join(":");

  if (loggedIntegrationSessionKey === sessionKey) {
    return;
  }
  loggedIntegrationSessionKey = sessionKey;

  const payload = {
    current: "Integration",
    activity: "session_start",
    meta,
    merchantId,
  };

  await logEvent(payload);
}

/** Fire-and-forget — must not block checkout or navigation. */
export function logActivityIntegrationStart(extra = {}) {
  void logActivityIntegrationStartInner(extra).catch(() => {
    /* best-effort */
  });
}
